import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import Colours from '@shared/ui/atoms/Colours';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { HeadlineMedium } from '@shared/ui/atoms/fonts/Headline';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import {
  loadSignageAway,
  loadSignageFloorPlan,
  loadSignageFloors,
  loadSignageOccupancy,
  selectSignage,
  selectSignageFloor,
  unpairThisSignageScreen,
} from '../data/signage.duck';
import { SignageSafetyPerson } from '../data/signage-service';
import { SignageDeskFocus, SignageDeskPing, SignageFloorMap, SignageFloorMapHandle } from './SignageFloorMap';
import { SignageMenuDrawer } from './SignageMenuDrawer';
import { SignageListFocus, SignagePeopleList } from './SignagePeopleList';
import { awayPeople, deskListKey, inOfficePeople, SignageListedPerson } from './signagePeople';
import { formatInZone } from './signageTime';
import { loadSignageView, saveSignageView, SignageViewMode } from '../storage/signageStorage';

const DEFAULT_REFRESH_INTERVAL_MS = 60_000;
const FLOOR_PLAN_REFRESH_INTERVAL_MS = 60 * 60_000;
// A page that runs for weeks in a wall-mounted browser gets a nightly clean
// slate, at a time no one is watching.
const DAILY_RELOAD_HOUR = 4;
const MAX_SEARCH_RESULTS = 6;
const STALE_AFTER_MISSED_REFRESHES = 3;
const MENU_AUTO_CLOSE_MS = 30_000;

const LIST_TITLES: Record<Exclude<SignageViewMode, 'map'>, string> = {
  office: 'In The Office Today',
  outOfOffice: 'Out of Office Today',
  workingElsewhere: 'Working Elsewhere Today',
};

export function SignageView() {
  const dispatch = useDispatch();
  const signage = useSelector(selectSignage);
  const [now, setNow] = useState(() => Date.now());
  const [menuOpen, setMenuOpen] = useState(false);
  const [view, setView] = useState<SignageViewMode>(loadSignageView);
  const [searchQuery, setSearchQuery] = useState('');
  const [focusDesk, setFocusDesk] = useState<SignageDeskFocus | null>(null);
  const [zoomPercent, setZoomPercent] = useState(100);
  const [pingDesks, setPingDesks] = useState<SignageDeskPing | null>(null);
  const [expandedSafetyRole, setExpandedSafetyRole] = useState<'firstAider' | 'fireWarden' | null>(null);
  const mapHandle = useRef<SignageFloorMapHandle | null>(null);

  // An expanded names list on an unattended screen folds itself away.
  useEffect(() => {
    if (expandedSafetyRole === null) return;
    const timeout = window.setTimeout(() => setExpandedSafetyRole(null), 15_000);
    return () => window.clearTimeout(timeout);
  }, [expandedSafetyRole]);

  // Same for the menu: a screen left with it open goes back to the full map.
  useEffect(() => {
    if (!menuOpen) return;
    const timeout = window.setTimeout(() => setMenuOpen(false), MENU_AUTO_CLOSE_MS);
    return () => window.clearTimeout(timeout);
  }, [menuOpen]);

  const refreshIntervalMs = signage.occupancy?.refreshIntervalMs || DEFAULT_REFRESH_INTERVAL_MS;

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    dispatch(loadSignageFloors());
  }, [dispatch]);

  const floorId = signage.selectedFloorId;

  useEffect(() => {
    if (floorId === null) return;
    dispatch(loadSignageFloorPlan({ floorId }));
    dispatch(loadSignageOccupancy({ floorId }));
    const occupancyInterval = window.setInterval(() => {
      dispatch(loadSignageOccupancy({ floorId }));
    }, refreshIntervalMs);
    const floorPlanInterval = window.setInterval(() => {
      dispatch(loadSignageFloorPlan({ floorId }));
      dispatch(loadSignageFloors());
    }, FLOOR_PLAN_REFRESH_INTERVAL_MS);
    return () => {
      window.clearInterval(occupancyInterval);
      window.clearInterval(floorPlanInterval);
    };
  }, [dispatch, floorId, refreshIntervalMs]);

  const awayView = view === 'outOfOffice' || view === 'workingElsewhere';

  // The away lists are office-wide and only fetched while one is on screen.
  useEffect(() => {
    if (!awayView) return;
    dispatch(loadSignageAway());
    const interval = window.setInterval(() => dispatch(loadSignageAway()), refreshIntervalMs);
    return () => window.clearInterval(interval);
  }, [dispatch, awayView, refreshIntervalMs]);

  // Nightly full reload.
  useEffect(() => {
    const next = new Date();
    next.setHours(DAILY_RELOAD_HOUR, 0, 0, 0);
    if (next.getTime() <= Date.now()) next.setDate(next.getDate() + 1);
    const timeout = window.setTimeout(() => window.location.reload(), next.getTime() - Date.now());
    return () => window.clearTimeout(timeout);
  }, []);

  // Browser-local time: the screen is physically in the office it shows, so
  // the device clock is the office's wall time.
  const officeClock = useMemo(() => formatInZone(now), [now]);
  const activePeriod: 'AM' | 'PM' = Number(officeClock.hour) < 12 ? 'AM' : 'PM';

  const occupancy = signage.occupancy;
  const currentFloor = signage.floors.find(f => f.id === floorId);

  // "Find a colleague": only offered when the company shows full names; the
  // occupancy payload carries no names in the other modes.
  // The away boards don't offer it: their people have no desk to point at.
  const searchable = occupancy?.nameDisplay === 'FULL_NAMES' && !awayView;
  const searchIndex = useMemo(() => {
    if (!searchable || !occupancy || !signage.floorPlan) return [];
    const deskLabels = new Map<number, string>(signage.floorPlan.desks.map(d => [d.id, d.label]));
    return occupancy.desks
      .map(d => {
        const occupant = activePeriod === 'AM' ? d.am : d.pm;
        if (!occupant?.displayName) return null;
        return { deskId: d.deskId, name: occupant.displayName, deskLabel: deskLabels.get(d.deskId) ?? '' };
      })
      .filter((entry): entry is { deskId: number; name: string; deskLabel: string } => entry !== null);
  }, [searchable, occupancy, signage.floorPlan, activePeriod]);

  const searchResults = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (query.length < 2) return [];
    return searchIndex
      .filter(entry => entry.name.toLowerCase().includes(query))
      .slice(0, MAX_SEARCH_RESULTS);
  }, [searchIndex, searchQuery]);

  const listFocus = useMemo<SignageListFocus | null>(
    () => focusDesk ? { key: deskListKey(focusDesk.deskId), nonce: focusDesk.nonce } : null,
    [focusDesk],
  );

  // What the list boards show; null on the map.
  const list = useMemo((): { heading: string; people: SignageListedPerson[]; message: string | null } | null => {
    if (view === 'map') return null;
    if (view === 'office') {
      const floorLabel = currentFloor?.label ?? '';
      if (!occupancy) return { heading: floorLabel, people: [], message: 'Loading…' };
      if (occupancy.nameDisplay === 'RESERVED_ONLY') {
        return { heading: floorLabel, people: [], message: "Names aren't shown on this screen. Map view shows which desks are taken." };
      }
      const people = inOfficePeople(occupancy, signage.floorPlan, floorLabel, activePeriod);
      return { heading: floorLabel, people, message: people.length === 0 ? 'No one is in on this floor yet.' : null };
    }
    const away = signage.away;
    if (!away) return { heading: '', people: [], message: 'Loading…' };
    if (away.nameDisplay === 'RESERVED_ONLY') return { heading: '', people: [], message: "Names aren't shown on this screen." };
    const people = awayPeople(away, view === 'outOfOffice' ? 'OUT_OF_OFFICE' : 'WORKING_ELSEWHERE', activePeriod);
    const heading = `${people.length} ${people.length === 1 ? 'person' : 'people'}`;
    if (people.length === 0) {
      return { heading, people, message: view === 'outOfOffice' ? 'No one is out of office.' : 'No one is working elsewhere.' };
    }
    return { heading, people, message: null };
  }, [view, currentFloor, occupancy, signage.floorPlan, signage.away, activePeriod]);

  // The map remounts at its fitted overview when shown again, so drop any
  // search pick, safety ping or zoom level left over from before.
  const switchView = (next: SignageViewMode) => {
    setMenuOpen(false);
    if (next === view) return;
    setView(next);
    saveSignageView(next);
    setSearchQuery('');
    setFocusDesk(null);
    setPingDesks(null);
    setExpandedSafetyRole(null);
    setZoomPercent(100);
  };

  const pickSearchResult = (deskId: number) => {
    setFocusDesk(prev => ({ deskId, nonce: (prev?.nonce ?? 0) + 1 }));
    setSearchQuery('');
  };

  // The pills only list role holders on the floor being shown; the expanded
  // card reveals everyone across the whole office, with their floor.
  const floorFirstAiders = occupancy?.safety.firstAiders.filter(p => p.floorId === floorId) ?? [];
  const floorFireWardens = occupancy?.safety.fireWardens.filter(p => p.floorId === floorId) ?? [];

  // Expanded list order: this floor first, then the other floors A-Z, then
  // people whose floor isn't known.
  const sortSafetyPeople = (people: SignageSafetyPerson[]): SignageSafetyPerson[] =>
    [...people].sort((a, b) => {
      const aOnFloor = a.floorId === floorId ? 0 : 1;
      const bOnFloor = b.floorId === floorId ? 0 : 1;
      if (aOnFloor !== bOnFloor) return aOnFloor - bOnFloor;
      if (a.floorLabel === b.floorLabel) return a.name.localeCompare(b.name);
      if (a.floorLabel === null) return 1;
      if (b.floorLabel === null) return -1;
      return a.floorLabel.localeCompare(b.floorLabel);
    });
  const sortedFirstAiders = sortSafetyPeople(occupancy?.safety.firstAiders ?? []);
  const sortedFireWardens = sortSafetyPeople(occupancy?.safety.fireWardens ?? []);

  // The current floor's people sit above a divider; everyone else below it.
  const renderSafetyRows = (people: SignageSafetyPerson[]) => {
    const onThisFloor = people.filter(p => p.floorId === floorId);
    const elsewhere = people.filter(p => p.floorId !== floorId);
    return (
      <>
        {onThisFloor.map(person => (
          <SafetyNameRow key={person.name}>
            <span>{person.name}</span>
            <SafetyFloorTag>{person.floorLabel ?? 'In office'}</SafetyFloorTag>
          </SafetyNameRow>
        ))}
        {onThisFloor.length > 0 && elsewhere.length > 0 && <SafetyDivider />}
        {elsewhere.map(person => (
          <SafetyNameRow key={person.name}>
            <span>{person.name}</span>
            <SafetyFloorTag>{person.floorLabel ?? 'In office'}</SafetyFloorTag>
          </SafetyNameRow>
        ))}
      </>
    );
  };

  // Tapping a safety pill pulses a ring on every desk on this floor where
  // someone holding that role is sitting, and expands the office-wide list.
  // A pill only renders when at least one person holds the role somewhere
  // in the office, so the list is never empty.
  const pingSafetyRole = (role: 'firstAider' | 'fireWarden', colour: string) => {
    setExpandedSafetyRole(prev => (prev !== role ? role : null));
    if (!occupancy) return;
    const deskIds = occupancy.desks
      .filter(d => {
        const occupant = activePeriod === 'AM' ? d.am : d.pm;
        return !!occupant && occupant[role];
      })
      .map(d => d.deskId);
    if (deskIds.length === 0) return;
    setPingDesks(prev => ({ deskIds, colour, nonce: (prev?.nonce ?? 0) + 1 }));
  };

  const isStale = signage.lastUpdatedAt !== null
    && now - signage.lastUpdatedAt > STALE_AFTER_MISSED_REFRESHES * refreshIntervalMs;
  const staleClock = signage.lastUpdatedAt !== null ? formatInZone(signage.lastUpdatedAt) : null;

  const desksInUse = occupancy
    ? (activePeriod === 'AM' ? occupancy.desksInUseAm : occupancy.desksInUsePm)
    : 0;
  const desksAvailable = occupancy ? Math.max(0, occupancy.desksTotal - desksInUse) : 0;

  if (signage.disabled) {
    return (
      <CenteredState>
        <StateCard>
          <HeadlineMedium>Signage is switched off</HeadlineMedium>
          <BodyRegular style={{ color: Colours.darkGrey, marginTop: 8 }}>
            A company admin can re-enable it in company settings.
          </BodyRegular>
        </StateCard>
      </CenteredState>
    );
  }

  if (signage.floorsLoaded && signage.floors.length === 0) {
    return (
      <CenteredState>
        <StateCard>
          <HeadlineMedium>No floor plans found</HeadlineMedium>
          <BodyRegular style={{ color: Colours.darkGrey, marginTop: 8 }}>
            Add a floor plan to this office to use signage.
          </BodyRegular>
          <UnpairLink onClick={() => dispatch(unpairThisSignageScreen())}>Unpair this screen</UnpairLink>
        </StateCard>
      </CenteredState>
    );
  }

  return (
    <Content>
      <SubHeader>
        <FloorControl>
          <FloorMenuButton onClick={() => setMenuOpen(true)} aria-label="Open menu">
            <Icon icon={IconTypes.Menu} />
          </FloorMenuButton>
          <FloorName>{view === 'map' ? currentFloor?.label ?? '' : LIST_TITLES[view]}</FloorName>
        </FloorControl>

        {searchable && (
          <SearchOverlay>
            <SearchRow>
              <SearchInput
                type="text"
                placeholder="Find a colleague"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
              <SearchButton aria-label="Search">
                <Icon icon={IconTypes.Search} color="white" />
              </SearchButton>
            </SearchRow>
            {searchResults.length > 0 && (
              <SearchResults>
                {searchResults.map(result => (
                  <SearchResultRow key={`${result.deskId}-${result.name}`} onClick={() => pickSearchResult(result.deskId)}>
                    <SearchResultName>{result.name}</SearchResultName>
                    <SearchResultDesk>{result.deskLabel}</SearchResultDesk>
                  </SearchResultRow>
                ))}
              </SearchResults>
            )}
          </SearchOverlay>
        )}

        {view === 'map' && (
          <ZoomControls>
            <ZoomButton onClick={() => mapHandle.current?.zoomIn()} aria-label="Zoom in">+</ZoomButton>
            <ZoomButton onClick={() => mapHandle.current?.zoomOut()} aria-label="Zoom out">−</ZoomButton>
            <ZoomPercent>{zoomPercent}%</ZoomPercent>
          </ZoomControls>
        )}
      </SubHeader>

      <ViewArea>
      {list ? (
        <SignagePeopleList
          // A new board or floor starts again from page one
          key={view === 'office' ? `office-${floorId}` : view}
          heading={list.heading}
          people={list.people}
          message={list.message}
          focus={view === 'office' ? listFocus : null}
          showSafetyRoles={view === 'office' && !!occupancy?.safety.enabled}
        />
      ) : (
        <>
          {signage.floorPlan ? (
            <>
              <SignageFloorMap
                ref={mapHandle}
                floorPlan={signage.floorPlan}
                occupancy={occupancy}
                activePeriod={activePeriod}
                currentTime={`${officeClock.hour}:${officeClock.minute}`}
                focusDesk={focusDesk}
                pingDesks={pingDesks}
                onZoomPercentChange={setZoomPercent}
              />
            </>
          ) : (
            <MapLoading>
              <BodyRegular style={{ color: Colours.darkGrey }}>Loading floor plan…</BodyRegular>
            </MapLoading>
          )}

          {/* Each pill only appears when someone holds the role somewhere in the office. */}
          {occupancy?.safety.enabled && occupancy.safety.firstAiders.length > 0 && (
            <TopLeftOverlay>
              <SafetyPill onClick={() => pingSafetyRole('firstAider', 'rgba(32, 204, 111, 0.6)')}>
                <SafetyIcon src="/assets/icons/first-aider.svg" alt="First aider" />
                <SafetyText>
                  {floorFirstAiders.length > 0
                    ? floorFirstAiders.map(p => p.name).join(', ')
                    : 'First aiders on other floors'}
                </SafetyText>
              </SafetyPill>
              {expandedSafetyRole === 'firstAider' && (
                <SafetyNamesCard>
                  {renderSafetyRows(sortedFirstAiders)}
                </SafetyNamesCard>
              )}
            </TopLeftOverlay>
          )}
          {occupancy?.safety.enabled && occupancy.safety.fireWardens.length > 0 && (
            <TopRightOverlay>
              <SafetyPill onClick={() => pingSafetyRole('fireWarden', 'rgba(240, 130, 34, 0.6)')}>
                <SafetyIcon src="/assets/icons/fire-warden.svg" alt="Fire warden" />
                <SafetyText>
                  {floorFireWardens.length > 0
                    ? floorFireWardens.map(p => p.name).join(', ')
                    : 'Fire wardens on other floors'}
                </SafetyText>
              </SafetyPill>
              {expandedSafetyRole === 'fireWarden' && (
                <SafetyNamesCard>
                  {renderSafetyRows(sortedFireWardens)}
                </SafetyNamesCard>
              )}
            </TopRightOverlay>
          )}

          <BottomLeft>
            {occupancy && (
              <StatCard>
                <StatNumber>{desksAvailable}</StatNumber>
                <StatLabel>of {occupancy.desksTotal} desks available</StatLabel>
              </StatCard>
            )}
          </BottomLeft>
        </>
      )}

      <BottomRight $inline={view === 'office'}>
        {isStale && staleClock && (
          <StaleChip>Last updated {staleClock.hour}:{staleClock.minute}</StaleChip>
        )}
        <PoweredBy>
          <PoweredByText>Powered by</PoweredByText>
          <PoweredByLogo src="/assets/images/logos/team-today-logo.svg" alt="Team Today" />
        </PoweredBy>
      </BottomRight>
      </ViewArea>

      <SignageMenuDrawer
        open={menuOpen}
        view={view}
        onSelectView={switchView}
        floors={signage.floors}
        selectedFloorId={floorId}
        onSelectFloor={id => {
          dispatch(selectSignageFloor(id));
          setMenuOpen(false);
          setSearchQuery('');
          setFocusDesk(null);
          // The away boards cover the whole office; picking a floor means its map.
          if (awayView) switchView('map');
        }}
        onClose={() => setMenuOpen(false)}
      />
    </Content>
  );
}

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

// The controls strip below the main header: floor picker, colleague search
// and zoom controls all sit on their own white band, not over the map.
const SubHeader = styled.div`
  position: relative;
  flex-shrink: 0;
  height: 48px;
  background: white;
  box-shadow: 0 9px 22px 0 rgba(46, 74, 148, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 24px;
  z-index: 12;
`;

const ViewArea = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  min-height: 0;
  background: white;
`;

const MapLoading = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FloorControl = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
`;

const FloorMenuButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: ${Colours.veryLightGrey};
  color: ${Colours.black};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
`;

const FloorName = styled.div`
  font-family: "soleil", sans-serif;
  font-weight: 700;
  font-size: 20px;
  color: ${Colours.black};
`;

const SearchOverlay = styled.div`
  position: absolute;
  top: 4px;
  left: 50%;
  transform: translateX(-50%);
  width: 380px;
  max-width: 45%;
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${Colours.veryLightGrey};
  border: 1px solid ${Colours.lightGrey};
  border-radius: 20px;
  padding: 3px 3px 3px 16px;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  font-family: "proxima-nova", sans-serif;
  font-size: 15px;
  color: ${Colours.black};
  background: transparent;
  &::placeholder {
    color: ${Colours.darkGrey};
  }
`;

const SearchButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: ${Colours.veryDarkBlue};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
`;

const SearchResults = styled.div`
  margin-top: 8px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 12px 32px 2px rgba(0, 87, 255, 0.15);
  overflow: hidden;
`;

const SearchResultRow = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border: none;
  background: white;
  text-align: left;
`;

const SearchResultName = styled.span`
  font-family: "proxima-nova", sans-serif;
  font-weight: 600;
  font-size: 16px;
  color: ${Colours.black};
`;

const SearchResultDesk = styled.span`
  font-family: "proxima-nova", sans-serif;
  font-size: 14px;
  color: ${Colours.darkGrey};
  flex-shrink: 0;
`;

const ZoomControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const ZoomButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid ${Colours.veryDarkBlue};
  background: white;
  color: ${Colours.veryDarkBlue};
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ZoomPercent = styled.div`
  padding: 6px 12px;
  border-radius: 18px;
  background: ${Colours.veryLightGrey};
  color: ${Colours.veryDarkBlue};
  font-family: "soleil", sans-serif;
  font-weight: 700;
  font-size: 14px;
  min-width: 52px;
  text-align: center;
`;

const BottomLeft = styled.div`
  position: absolute;
  left: 24px;
  bottom: 24px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
`;

const TopLeftOverlay = styled.div`
  position: absolute;
  top: 16px;
  left: 24px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const TopRightOverlay = styled.div`
  position: absolute;
  top: 16px;
  right: 24px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
`;

const SafetyNamesCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 12px 32px 2px rgba(0, 87, 255, 0.15);
  padding: 6px 0;
  min-width: 220px;
  max-height: 320px;
  overflow-y: auto;
`;

const SafetyNameRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 9px 18px;
  font-family: "proxima-nova", sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${Colours.black};
  white-space: nowrap;
`;

const SafetyDivider = styled.div`
  border-top: 1px solid ${Colours.lightGrey};
  margin: 4px 0;
`;

const SafetyFloorTag = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${Colours.darkGrey};
  background: ${Colours.veryLightGrey};
  border-radius: 10px;
  padding: 3px 10px;
  flex-shrink: 0;
`;

const SafetyPill = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  border-radius: 24px;
  padding: 8px 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  max-width: 420px;
`;

const SafetyIcon = styled.img`
  width: 22px;
  height: 22px;
  flex-shrink: 0;
`;

const SafetyText = styled.span`
  font-family: "proxima-nova", sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${Colours.black};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatCard = styled.div`
  background: ${Colours.veryDarkBlue};
  color: white;
  border-radius: 12px;
  padding: 16px 22px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

const StatNumber = styled.span`
  font-family: "soleil", sans-serif;
  font-weight: 700;
  font-size: 40px;
  line-height: 40px;
  font-variant-numeric: tabular-nums;
`;

const StatLabel = styled.span`
  font-family: "proxima-nova", sans-serif;
  font-size: 15px;
  opacity: 0.85;
  white-space: nowrap;
`;

// Over the office list the chips sit side by side, so together they stay no
// taller than the list's bottom padding and never cover a name.
const BottomRight = styled.div<{ $inline: boolean }>`
  position: absolute;
  right: 24px;
  bottom: 24px;
  z-index: 10;
  display: flex;
  flex-direction: ${p => p.$inline ? 'row' : 'column'};
  align-items: ${p => p.$inline ? 'center' : 'flex-end'};
  gap: 10px;
`;

const StaleChip = styled.div`
  padding: 4px 14px;
  border-radius: 16px;
  background: white;
  color: ${Colours.orange};
  font-family: "proxima-nova", sans-serif;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
`;

const PoweredBy = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  border-radius: 20px;
  padding: 8px 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
`;

const PoweredByText = styled.span`
  font-family: "proxima-nova", sans-serif;
  font-size: 13px;
  color: ${Colours.darkGrey};
`;

const PoweredByLogo = styled.img`
  height: 16px;
  width: auto;
  display: block;
`;

const CenteredState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
`;

const StateCard = styled.div`
  background: white;
  border-radius: 20px;
  box-shadow: 0 12px 32px 2px rgba(0, 87, 255, 0.15);
  padding: 32px;
  max-width: 520px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const UnpairLink = styled.button`
  margin-top: 24px;
  background: none;
  border: none;
  color: ${Colours.darkGrey};
  text-decoration: underline;
  cursor: pointer;
  font-size: 16px;
`;
